import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "@/lib/format";
import { Upload, Link2, Unlink, FileSpreadsheet, FileText, ChevronRight, Bookmark, Wand2, Sparkles, Check, Zap, Layers, X, Save } from "lucide-react";

const TARGET_LABELS = {
  date: "Date *",
  description: "Description / Narration *",
  debit: "Debit / Withdrawal",
  credit: "Credit / Deposit",
  amount: "Signed Amount (+ = credit, − = debit)",
  type_column: "Type Column (Cr / Dr indicator)",
  balance: "Running Balance",
  reference: "Reference / UTR / Cheque No",
};

export default function Reconcile() {
  const [banks, setBanks] = useState([]);
  const [selBank, setSelBank] = useState("");
  const [entries, setEntries] = useState([]);
  const [openImport, setOpenImport] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [pickForEntry, setPickForEntry] = useState(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [saveMapping, setSaveMapping] = useState(true);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [mappingSource, setMappingSource] = useState(""); // "saved" | "preset" | "auto"
  const [suggestions, setSuggestions] = useState({});    // entry_id → suggestion object
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(null);       // inline "Save as rule" from a row
  const [accountsCache, setAccountsCache] = useState([]);
  const fileRef = useRef(null);

  // Iter-132 · Multi-bank batch upload
  const [openBatch, setOpenBatch] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]);        // File[]
  const [batchPreview, setBatchPreview] = useState(null);  // { files: [...], accounts: [...] }
  const [batchBusy, setBatchBusy] = useState(false);
  const batchFileRef = useRef(null);

  useEffect(() => {
    api.get("/accounts").then((r) => {
      const b = r.data.filter((a) => a.subtype === "bank" || a.subtype === "cash");
      setBanks(b);
      setAccountsCache(r.data);
      if (b.length && !selBank) setSelBank(b[0].id);
    });
    api.get("/bank/presets").then((r) => setPresets(r.data.presets || []));
  }, []);

  const load = async () => {
    if (!selBank) return;
    setEntries((await api.get("/bank/entries", { params: { bank_account_id: selBank } })).data);
    setSuggestions({});    // suggestions are per-load
  };
  useEffect(() => { load(); }, [selBank]);

  // ---- Iter-127: AI auto-categorize unmatched rows ----
  const runSuggest = async () => {
    if (!selBank) return;
    setSuggestBusy(true);
    try {
      const r = await api.post("/bank/entries/suggestions", { bank_account_id: selBank });
      const map = {};
      let hits = 0;
      for (const s of r.data) {
        map[s.entry_id] = s;
        if (s.suggested_account_id) hits++;
      }
      setSuggestions(map);
      toast.success(`AI scanned ${r.data.length} unmatched row(s) — ${hits} confident suggestion(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    }
    setSuggestBusy(false);
  };

  const acceptSuggestion = async (entry_id) => {
    const s = suggestions[entry_id];
    if (!s?.suggested_account_id) return;
    try {
      const r = await api.post(`/bank/entries/${entry_id}/auto-post`, { account_id: s.suggested_account_id });
      toast.success(`Posted ${r.data.voucher_number} · ${r.data.voucher_type} for ${fmtINR(r.data.amount)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    }
  };

  // ---- Iter-129: "Save as rule" from an unmatched row ----
  const openRuleFromRow = async (entry) => {
    try {
      const r = await api.post(`/bank/rules/suggest-from-entry/${entry.id}`);
      setRuleDraft({
        entry_id:    entry.id,
        name:        r.data.name || (entry.description || "").slice(0, 40),
        patterns:    [r.data.pattern || (entry.description || "").slice(0, 40)],
        side:        r.data.side || (entry.debit > 0 ? "debit" : "credit"),
        account_id:  r.data.suggested_account?.id || "",
        priority:    100,
        enabled:     true,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to prefill rule");
    }
  };

  const saveRuleFromRow = async () => {
    if (!ruleDraft.name || !ruleDraft.account_id || !ruleDraft.patterns.filter(Boolean).length) {
      toast.error("Name, pattern, and account are required");
      return;
    }
    try {
      await api.post("/bank/rules", {
        name:       ruleDraft.name,
        patterns:   ruleDraft.patterns.filter(Boolean),
        side:       ruleDraft.side,
        account_id: ruleDraft.account_id,
        priority:   ruleDraft.priority,
        enabled:    ruleDraft.enabled,
      });
      toast.success(`Rule "${ruleDraft.name}" saved. Click "AI Suggest" or run rules to apply.`);
      // Also apply immediately to this entry
      await api.post(`/bank/entries/${ruleDraft.entry_id}/auto-post`, {
        account_id: ruleDraft.account_id,
      }).catch(() => {});
      setRuleDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save rule");
    }
  };

  // ---- Iter-142 · Create Voucher from an unmatched bank entry ----
  // Full voucher form (voucher type, party, splits, cost centre, reference, TDS)
  // that atomically posts a voucher and links it to this bank row.
  const [voucherFor, setVoucherFor] = useState(null);     // entry object
  const [vForm, setVForm] = useState(null);               // { voucher_type, date, party_id, reference, narration, lines: [...] }
  const [vBusy, setVBusy] = useState(false);
  const [partiesCache, setPartiesCache] = useState([]);
  const [costCentresCache, setCostCentresCache] = useState([]);
  // Iter-143 state
  const [openBills, setOpenBills] = useState([]);         // fetched from /parties/{id}/open-docs
  const [openBillsBusy, setOpenBillsBusy] = useState(false);
  const [pickedBills, setPickedBills] = useState({});     // { doc_id: true }
  const [tdsApplied, setTdsApplied] = useState(false);
  const [tdsInfo, setTdsInfo] = useState(null);            // { section, rate, payable_account_id, payable_account_name }
  const [importResult, setImportResult] = useState(null);  // post-import success dialog state
  // Iter-144 · AI Smart Match — auto-tick bills that subset-sum to bank amount
  const [smartMatchInfo, setSmartMatchInfo] = useState(null); // { count, total, exact, mode }
  // Bank Memory · auto-fill hint  { acc:{id,code,name}, count, last_used_at, autofilled:bool }
  const [memoryHint, setMemoryHint] = useState(null);
  useEffect(() => {
    api.get("/parties").then((r) => setPartiesCache(r.data || [])).catch(() => {});
    api.get("/dimensions").then((r) => setCostCentresCache(r.data || [])).catch(() => {});
  }, []);

  // Whenever the picked party changes, fetch their open bills + detect TDS applicability.
  useEffect(() => {
    if (!voucherFor || !vForm?.party_id) {
      setOpenBills([]); setPickedBills({}); setTdsInfo(null); setTdsApplied(false); setSmartMatchInfo(null); setMemoryHint(null); return;
    }
    setOpenBillsBusy(true);
    api.get(`/parties/${vForm.party_id}/open-docs`).then((r) => {
      setOpenBills(r.data?.docs || []);
      const party = r.data?.party || partiesCache.find((p) => p.id === vForm.party_id) || {};
      if (party.default_tds_section && party.default_tds_rate) {
        const acc = accountsCache.find((a) =>
          (a.name || "").toLowerCase().includes(`sec ${party.default_tds_section.toLowerCase()}`)
        );
        setTdsInfo({ section: party.default_tds_section, rate: party.default_tds_rate,
                       payable_account_id: acc?.id, payable_account_name: acc?.name });
      } else {
        setTdsInfo(null); setTdsApplied(false);
      }
    }).catch(() => setOpenBills([]))
      .finally(() => setOpenBillsBusy(false));

    // Bank Memory — remembered counter account for this party.
    const direction = (voucherFor.debit || 0) > 0 ? "out" : "in";
    api.get("/bank/party-memory", { params: { party_id: vForm.party_id, direction } })
      .then((r) => {
        const memo = r.data?.memory;
        if (!memo || !memo.counter_account) { setMemoryHint(null); return; }
        const acc = memo.counter_account;
        // Auto-fill counter leg (line[1]) only if the user hasn't picked one yet.
        let didFill = false;
        setVForm((f) => {
          if (!f || !f.lines || f.lines.length < 2 || f.lines[1]?.account_id) return f;
          const lines = [...f.lines];
          lines[1] = { ...lines[1], account_id: acc.id };
          didFill = true;
          return { ...f, lines };
        });
        setMemoryHint({ acc, count: memo.count, last_used_at: memo.last_used_at, autofilled: didFill });
      })
      .catch(() => setMemoryHint(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherFor?.id, vForm?.party_id]);

  // Iter-144 · AI Smart Match — try subset-sum on open bills; if a combo
  // matches the bank amount exactly, auto-tick those rows so the user just
  // taps "Allocate selected".
  const subsetSumMatch = (bills, target, eps = 0.01) => {
    const n = bills.length;
    if (!n) return null;
    const amounts = bills.map((b) => Number(b.balance_due || 0));
    // 1. Any single bill exactly matches?
    for (let i = 0; i < n; i++) if (Math.abs(amounts[i] - target) < eps) return { picked: [bills[i]], mode: "single" };
    // 2. All bills together?
    const total = amounts.reduce((a, b) => a + b, 0);
    if (Math.abs(total - target) < eps) return { picked: bills, mode: "all" };
    // 3. Brute-force subset scan capped at 12 bills (2^12 = 4096 iterations)
    const limit = Math.min(n, 12);
    const combos = 1 << limit;
    let best = null;
    for (let mask = 1; mask < combos; mask++) {
      let sum = 0;
      const picked = [];
      for (let i = 0; i < limit; i++) {
        if (mask & (1 << i)) { sum += amounts[i]; picked.push(bills[i]); }
      }
      if (Math.abs(sum - target) < eps) {
        if (!best || picked.length < best.length) best = picked;
      }
    }
    return best ? { picked: best, mode: "subset" } : null;
  };

  useEffect(() => {
    if (!openBills.length || !voucherFor) { setSmartMatchInfo(null); return; }
    const bankAmt = voucherFor.debit || voucherFor.credit || 0;
    if (bankAmt <= 0) { setSmartMatchInfo(null); return; }
    const match = subsetSumMatch(openBills, bankAmt);
    if (match && match.picked?.length) {
      const picked = {};
      match.picked.forEach((b) => (picked[b.id] = true));
      setPickedBills(picked);
      setSmartMatchInfo({
        count: match.picked.length,
        total: match.picked.reduce((a, b) => a + (b.balance_due || 0), 0),
        exact: true,
        mode: match.mode,
      });
    } else {
      setSmartMatchInfo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBills, voucherFor?.id]);

  const openCreateVoucher = (entry) => {
    const isOut = (entry.debit || 0) > 0;
    const amt = entry.debit || entry.credit || 0;
    // Prefill: one bank leg on the correct side + one blank counter leg to be filled by user
    const bankLeg = isOut
      ? { account_id: entry.bank_account_id, debit: 0, credit: amt, narration: entry.description || "" }
      : { account_id: entry.bank_account_id, debit: amt, credit: 0, narration: entry.description || "" };
    const counterLeg = isOut
      ? { account_id: "", debit: amt, credit: 0, narration: "", party_id: "", cost_center_id: "" }
      : { account_id: "", debit: 0, credit: amt, narration: "", party_id: "", cost_center_id: "" };
    setVoucherFor(entry);
    setVForm({
      voucher_type: isOut ? "payment" : "receipt",
      date: entry.date,
      reference: entry.reference || "",
      narration: entry.description || "",
      party_id: "",
      // Bank leg is always first & locked; counter legs are user-editable and can be split
      lines: [bankLeg, counterLeg],
    });
  };

  const closeCreateVoucher = () => {
    setVoucherFor(null); setVForm(null); setVBusy(false);
    setOpenBills([]); setPickedBills({}); setTdsInfo(null); setTdsApplied(false); setSmartMatchInfo(null);
    setMemoryHint(null);
  };

  // Toggle TDS auto-deduct — gross-up the counter line + inject Cr TDS Payable
  // Formula (payment voucher): bank_paid = gross - tds → gross = bank_paid / (1 - rate/100)
  const toggleTds = () => {
    if (!tdsInfo || !voucherFor || !vForm) return;
    if (tdsApplied) {
      // Remove TDS line + restore counter Dr to bank amount
      setVForm((f) => {
        const bankAmt = voucherFor.debit || voucherFor.credit || 0;
        const isOut = (voucherFor.debit || 0) > 0;
        const cleaned = f.lines.filter((l) => l.account_id !== tdsInfo.payable_account_id);
        // Reset the first counter line to bank amount
        cleaned[1] = { ...cleaned[1], debit: isOut ? bankAmt : 0, credit: isOut ? 0 : bankAmt };
        return { ...f, lines: cleaned };
      });
      setTdsApplied(false);
      toast.info("TDS deduction removed — counter line restored to bank amount");
    } else {
      if (!tdsInfo.payable_account_id) { toast.error(`TDS Payable — Sec ${tdsInfo.section} ledger not found in Chart of Accounts`); return; }
      const bankAmt = voucherFor.debit || voucherFor.credit || 0;
      const isOut = (voucherFor.debit || 0) > 0;
      const gross = +(bankAmt / (1 - tdsInfo.rate / 100)).toFixed(2);
      const tds = +(gross - bankAmt).toFixed(2);
      setVForm((f) => {
        const lines = [...f.lines];
        // Update first counter line to gross
        lines[1] = { ...lines[1], debit: isOut ? gross : 0, credit: isOut ? 0 : gross };
        // Append TDS Payable line (Cr for payment / Dr for receipt would be unusual - only payment side supported)
        const tdsLine = {
          account_id: tdsInfo.payable_account_id,
          debit: isOut ? 0 : tds, credit: isOut ? tds : 0,
          narration: `TDS Sec ${tdsInfo.section} @ ${tdsInfo.rate}%`,
          party_id: f.party_id || "",
          cost_center_id: "",
        };
        return { ...f, lines: [...lines, tdsLine] };
      });
      setTdsApplied(true);
      toast.success(`TDS ${tdsInfo.section} @ ${tdsInfo.rate}% applied — gross ₹${gross.toLocaleString("en-IN")}, TDS ₹${tds.toLocaleString("en-IN")}`);
    }
  };

  // Allocate picked bills onto counter lines via against_invoice_id + auto-sum amounts
  const applyPickedBills = () => {
    const picked = openBills.filter((b) => pickedBills[b.id]);
    if (!picked.length) { toast.warning("Tick at least one bill to allocate"); return; }
    const isOut = (voucherFor.debit || 0) > 0;
    const bankAmt = voucherFor.debit || voucherFor.credit || 0;
    const totalOwed = picked.reduce((a, b) => a + (b.balance_due || 0), 0);
    if (Math.abs(totalOwed - bankAmt) > 0.01) {
      toast.warning(`Picked bills total ₹${totalOwed.toFixed(2)} ≠ bank amount ₹${bankAmt.toFixed(2)} — you may need to split further or edit lines.`);
    }
    setVForm((f) => {
      // Keep bank leg (line 0). Replace all counter lines with one per picked bill.
      const bankLeg = f.lines[0];
      const counterLegs = picked.map((b) => ({
        account_id: "",  // user must pick account (e.g. Sundry Creditors control) for each
        debit: isOut ? b.balance_due : 0,
        credit: isOut ? 0 : b.balance_due,
        narration: `${b.doc_type === "sales_invoice" ? "Rcvd against inv" : "Paid against bill"} ${b.doc_no}`,
        party_id: f.party_id,
        cost_center_id: "",
        against_invoice_id: b.id,
        _bill_ref: b.doc_no,  // display-only
      }));
      // If Sundry Creditors / Debtors control account is set on the party, auto-fill that account
      const party = partiesCache.find((p) => p.id === f.party_id);
      const ctrlAcc = party?.ledger_account_id;
      if (ctrlAcc) counterLegs.forEach((l) => (l.account_id = ctrlAcc));
      return { ...f, lines: [bankLeg, ...counterLegs] };
    });
    toast.success(`${picked.length} bill(s) allocated — review the ledger lines below`);
  };

  // Update a line in-place. `idx=0` is the locked bank leg; only narration is editable there.
  const setLine = (idx, patch) => setVForm((f) => ({
    ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const addSplitLine = () => setVForm((f) => {
    const isOut = (voucherFor?.debit || 0) > 0;
    const blank = isOut
      ? { account_id: "", debit: 0, credit: 0, narration: "", party_id: "", cost_center_id: "" }
      : { account_id: "", debit: 0, credit: 0, narration: "", party_id: "", cost_center_id: "" };
    return { ...f, lines: [...f.lines, blank] };
  });

  const removeSplitLine = (idx) => setVForm((f) => ({
    ...f, lines: f.lines.filter((_, i) => i !== idx || i === 0),  // never remove line 0 (bank leg)
  }));

  const commitCreateVoucher = async () => {
    if (!voucherFor || !vForm) return;
    // Client-side validation to keep the API 400 rate down
    const totalDr = vForm.lines.reduce((a, l) => a + (Number(l.debit) || 0), 0);
    const totalCr = vForm.lines.reduce((a, l) => a + (Number(l.credit) || 0), 0);
    if (Math.abs(totalDr - totalCr) > 0.01) { toast.error(`Unbalanced — Dr ₹${totalDr.toFixed(2)} vs Cr ₹${totalCr.toFixed(2)}`); return; }
    if (vForm.lines.some((l, i) => i > 0 && !l.account_id)) { toast.error("Every non-bank line needs an account"); return; }
    setVBusy(true);
    try {
      const body = {
        voucher_type: vForm.voucher_type,
        date: vForm.date, reference: vForm.reference, narration: vForm.narration,
        party_id: vForm.party_id || undefined,
        lines: vForm.lines.map((l) => ({
          account_id: l.account_id,
          debit: Number(l.debit) || 0, credit: Number(l.credit) || 0,
          narration: l.narration || vForm.narration,
          party_id: l.party_id || vForm.party_id || undefined,
          cost_center_id: l.cost_center_id || undefined,
        })),
      };
      const r = await api.post(`/bank/entries/${voucherFor.id}/create-voucher`, body);
      if (r.data.approval_status === "pending") {
        toast.success(`Voucher ${r.data.voucher_number} created — queued for approval`);
      } else {
        toast.success(`Voucher ${r.data.voucher_number} posted & linked to this bank row`);
      }
      closeCreateVoucher(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create voucher"); }
    finally { setVBusy(false); }
  };

  const resetWizard = () => {
    setStep(1); setFile(null); setPreview(null); setMapping({});
    setSelectedPreset(""); setMappingSource(""); setBusy(false); setSaveMapping(true);
    if (fileRef.current) fileRef.current.value = "";
  };
  const closeWizard = () => { setOpenImport(false); resetWizard(); };

  // Iter-132 · Multi-bank batch upload handlers
  const resetBatch = () => {
    setBatchFiles([]); setBatchPreview(null); setBatchBusy(false);
    if (batchFileRef.current) batchFileRef.current.value = "";
  };
  const closeBatch = () => { setOpenBatch(false); resetBatch(); };

  const runBatchPreview = async (files) => {
    if (!files || files.length === 0) return;
    setBatchFiles(Array.from(files));
    setBatchBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const r = await api.post("/bank/batch-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Add save_mapping default per file
      const withDefaults = (r.data.files || []).map((f) => ({
        ...f,
        save_mapping: !f.saved_mapping,       // only save if we DIDN'T already have one
      }));
      setBatchPreview({ ...r.data, files: withDefaults });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch preview failed");
      setBatchFiles([]);
    }
    setBatchBusy(false);
  };

  const setBatchFile = (idx, patch) => {
    setBatchPreview((p) => ({
      ...p,
      files: p.files.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  };

  const applyPresetToBatchFile = (idx, presetId) => {
    const p = presets.find((x) => x.id === presetId);
    if (!p) return;
    setBatchFile(idx, { mapping_override: { ...p.mapping }, applied_preset: p.name });
  };

  const commitBatch = async () => {
    if (!batchPreview) return;
    const usable = batchPreview.files.filter((f) => !f.error && f.matched_bank_account_id);
    if (usable.length === 0) {
      toast.error("Pick a bank account for at least one file first");
      return;
    }
    setBatchBusy(true);
    try {
      const fd = new FormData();
      batchFiles.forEach((f) => fd.append("files", f));
      const plans = batchPreview.files
        .filter((f) => !f.error)
        .map((f) => ({
          filename:          f.filename,
          bank_account_id:   f.matched_bank_account_id || null,
          mapping:           f.mapping_override || f.saved_mapping || f.suggested_mapping || {},
          save_mapping:      !!f.save_mapping,
        }));
      fd.append("plans", JSON.stringify(plans));
      const r = await api.post("/bank/batch-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const results = r.data.files || [];
      const totals = r.data.totals || {};
      const okCount = results.filter((x) => !x.skipped).length;
      const skip = results.filter((x) => x.skipped).length;
      const totalMatched = totals.total_matched || 0;
      const totalUnmatched = totals.total_unmatched || 0;
      const totalAuto = totals.total_auto_posted || 0;
      let msg = `Imported ${okCount} file(s) · ${totalMatched + totalUnmatched} rows · auto-matched ${totalMatched}`;
      if (totalAuto) msg += ` · ${totalAuto} auto-posted`;
      if (skip) msg += ` · ${skip} skipped`;
      toast.success(msg);
      const skippedFiles = results.filter((x) => x.skipped);
      if (skippedFiles.length) {
        skippedFiles.forEach((s) => toast.warning(`${s.filename}: ${s.reason}`));
      }
      closeBatch();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch import failed");
    }
    setBatchBusy(false);
  };

  const applyPreset = (presetId) => {
    setSelectedPreset(presetId);
    if (!presetId) return;
    const p = presets.find((x) => x.id === presetId);
    if (p) {
      setMapping(p.mapping);
      setMappingSource("preset");
      toast.success(`Applied ${p.name} preset`);
    }
  };

  const onFilePick = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (selBank) fd.append("bank_account_id", selBank);
      const r = await api.post("/bank/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      // Priority: saved mapping → matched preset → suggested
      if (r.data.saved_mapping && Object.keys(r.data.saved_mapping).length) {
        setMapping(r.data.saved_mapping);
        setMappingSource("saved");
      } else if (r.data.matched_preset) {
        setMapping(r.data.matched_preset.mapping);
        setSelectedPreset(r.data.matched_preset.id);
        setMappingSource("preset");
      } else {
        setMapping(r.data.suggested_mapping || {});
        setMappingSource("auto");
      }
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse file");
    }
    setBusy(false);
  };

  const commitImport = async () => {
    if (!file || !selBank) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bank_account_id", selBank);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("save_mapping", saveMapping ? "true" : "false");
      const r = await api.post("/bank/import-mapped", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { matched, unmatched, parse_errors, mapping_saved, auto_posted, queued_for_approval } = r.data;
      let msg = `Imported ${matched + unmatched} rows · auto-matched ${matched}`;
      if (auto_posted) msg += ` · rules auto-posted ${auto_posted}`;
      if (queued_for_approval) msg += ` · ${queued_for_approval} queued for approval`;
      if (mapping_saved) msg += ` · mapping saved`;
      // Iter-143 · Show a summary dialog instead of silently closing — gives the
      // accountant one-tap shortcuts to AI Suggest & jump to Create Vouchers.
      setImportResult({
        matched: matched || 0, unmatched: unmatched || 0,
        auto_posted: auto_posted || 0,
        queued_for_approval: queued_for_approval || 0,
        parse_errors_count: (parse_errors || []).length,
        mapping_saved: !!mapping_saved,
      });
      toast.success(msg);
      if (parse_errors?.length) toast.warning(`${parse_errors.length} row(s) skipped — review columns.`);
      closeWizard(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  // ---- Save mapping only (no import) ----
  const saveMappingOnly = async () => {
    if (!selBank) { toast.error("Pick a bank account first"); return; }
    if (!mapping.date || !mapping.description) {
      toast.error("Map at least Date and Description before saving");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/accounts/${selBank}/bank-mapping`, { mapping });
      toast.success("Mapping saved. Next upload will auto-fill from this template.");
      closeWizard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save mapping");
    }
    setBusy(false);
  };

  const openCandidates = async (entry) => {
    setPickForEntry(entry);
    const amt = entry.debit - entry.credit;
    const r = await api.get("/bank/candidates", { params: { bank_account_id: selBank, amount: amt } });
    setCandidates(r.data);
  };
  const match = async (v_id) => {
    try { await api.post(`/bank/entries/${pickForEntry.id}/match`, { voucher_id: v_id }); toast.success("Matched"); setPickForEntry(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unmatch = async (id) => {
    try { await api.post(`/bank/entries/${id}/unmatch`); toast.success("Unmatched"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const matched = entries.filter((e) => e.status === "matched").length;
  const unmatched = entries.length - matched;
  const mappingComplete = mapping.date && mapping.description &&
    ((mapping.debit && mapping.credit) || mapping.amount);

  const isPdf = file && /\.pdf$/i.test(file.name);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reconcile-page">
      <PageHeader
        eyebrow="Cash Management"
        title="Bank Reconciliation"
        description="Upload your bank statement in ANY format (.csv/.xls/.xlsx/.pdf). Pick a preset (HDFC · ICICI · Axis · SBI · Kotak · Yes · IDFC · IndusInd · RBL · PNB), or map columns once — the system remembers per bank."
        actions={
          <>
            <Select value={selBank} onValueChange={setSelBank}>
              <SelectTrigger className="rounded-sm h-9 w-56" data-testid="recon-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}><span className="font-mono text-xs mr-2">{b.code}</span>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={runSuggest} disabled={suggestBusy || !selBank || unmatched === 0}
                    variant="outline" className="rounded-sm gap-1.5"
                    data-testid="ai-suggest-btn">
              <Sparkles className="w-3.5 h-3.5" />{suggestBusy ? "Scanning…" : "AI Suggest"}
            </Button>
            <Button onClick={() => setOpenImport(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="import-stmt-btn"><Upload className="w-3.5 h-3.5" /> Import Statement</Button>
            <Button onClick={() => setOpenBatch(true)} variant="outline" className="rounded-sm gap-1.5" data-testid="batch-import-btn"><Layers className="w-3.5 h-3.5" /> Batch Upload</Button>
          </>
        }
      />

      <div className="grid grid-cols-3 gap-4">
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Statement lines</div><div className="metric-value text-2xl mt-2">{entries.length}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Auto-matched</div><div className="metric-value text-2xl mt-2 text-emerald-600">{matched}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Needs attention</div><div className="metric-value text-2xl mt-2 text-accent">{unmatched}</div></div>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>Description</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th>Matched Voucher</th><th></th></tr></thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">Import a statement to begin.</td></tr>}
            {entries.map((e) => {
              const sug = suggestions[e.id];
              return (
              <tr key={e.id} data-testid={`stmt-row-${e.id}`}>
                <td className="text-xs">{fmtDate(e.date)}</td>
                <td className="text-xs">
                  {e.description}
                  {e.status !== "matched" && sug?.suggested_account_id && (
                    <div className="text-[10px] mt-0.5">
                      <Sparkles className="inline w-3 h-3 text-accent mr-0.5" />
                      <span className="text-muted-foreground">Suggest:</span>{" "}
                      <span className="font-mono">{sug.suggested_account_code}</span>{" "}
                      <span>{sug.suggested_account_name}</span>{" "}
                      <span className={sug.confidence === "high" ? "text-emerald-600" : "text-amber-600"}>({sug.confidence})</span>
                    </div>
                  )}
                </td>
                <td className="text-right tabular-nums">{e.debit ? fmtINR(e.debit) : "—"}</td>
                <td className="text-right tabular-nums">{e.credit ? fmtINR(e.credit) : "—"}</td>
                <td className="text-xs font-mono">{e.matched_voucher_number ? <span className="text-emerald-600">{e.matched_voucher_number}</span> :
                   e.status === "pending_approval" ? (
                     <span className="text-amber-600 inline-flex items-center gap-1"
                            title={`Awaiting approval from ${e.approver_name || "designated approver"}`}
                            data-testid={`pending-appr-${e.id}`}>
                       ⏳ Awaiting approval
                       {e.approver_name && <span className="text-[10px] font-normal text-muted-foreground">· {e.approver_name}</span>}
                     </span>
                   ) : <span className="text-warning">Unmatched</span>}</td>
                <td className="text-right">
                  {e.status === "matched" ?
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => unmatch(e.id)}><Unlink className="w-3 h-3" /> Unmatch</Button> :
                   e.status === "pending_approval" ?
                    <span className="text-[10px] text-muted-foreground italic">In approval</span> :
                    <div className="flex gap-1 justify-end">
                      {sug?.suggested_account_id && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10"
                                onClick={() => acceptSuggestion(e.id)}
                                data-testid={`accept-sug-${e.id}`}>
                          <Check className="w-3 h-3" /> Accept
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                              onClick={() => openRuleFromRow(e)}
                              title="Save this as a recurring bank rule"
                              data-testid={`save-rule-${e.id}`}>
                        <Zap className="w-3 h-3" /> Save as rule
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-accent hover:text-accent hover:bg-accent/10"
                              onClick={() => openCreateVoucher(e)}
                              title="Create a new payment/receipt/journal voucher and link it to this bank row"
                              data-testid={`create-voucher-${e.id}`}>
                        <FileText className="w-3 h-3" /> Create Voucher
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => openCandidates(e)} data-testid={`match-btn-${e.id}`}><Link2 className="w-3 h-3" /> Match</Button>
                    </div>}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* ---- Import Wizard ---- */}
      <Dialog open={openImport} onOpenChange={(o) => o ? setOpenImport(true) : closeWizard()}>
        <DialogContent className="max-w-3xl" data-testid="import-wizard">
          <DialogHeader>
            <DialogTitle>Import Bank Statement</DialogTitle>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span className={step === 1 ? "text-accent font-medium" : ""}>1. Upload</span>
              <ChevronRight className="w-3 h-3" />
              <span className={step === 2 ? "text-accent font-medium" : ""}>2. Map columns</span>
              <ChevronRight className="w-3 h-3" />
              <span className={step === 3 ? "text-accent font-medium" : ""}>3. Preview & Import</span>
            </div>
          </DialogHeader>

          {/* Step 1 — Upload */}
          {step === 1 && (
            <div className="space-y-4 py-4">
              <label
                onClick={() => fileRef.current?.click()}
                className="cursor-pointer flex flex-col items-center justify-center gap-3 h-40 border-2 border-dashed rounded-sm bg-accent/5 hover:bg-accent/10 transition-colors"
                data-testid="bank-file-drop"
              >
                <div className="flex gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-accent" />
                  <FileText className="w-8 h-8 text-accent" />
                </div>
                <div className="text-sm">
                  <span className="text-accent font-medium">Click to upload</span> your bank statement
                </div>
                <div className="text-xs text-muted-foreground">Accepts .csv · .xls · .xlsx · <b>.pdf</b> — any layout</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xls,.xlsx,.txt,.pdf"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && onFilePick(e.target.files[0])}
                  data-testid="bank-file-input"
                />
              </label>
              {busy && (
                <div className="text-center text-xs">
                  {isPdf ? (
                    <span className="text-accent"><Sparkles className="inline w-3 h-3 mr-1" />Reading your PDF with AI OCR — this may take 30-60 seconds…</span>
                  ) : (
                    <span className="text-muted-foreground">Parsing file…</span>
                  )}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                <b>No template needed.</b> PDFs are read with AI OCR. CSV/XLS/XLSX are parsed directly.
                Your column layout is auto-detected and remembered per bank.
              </div>
            </div>
          )}

          {/* Step 2 — Map columns */}
          {step === 2 && preview && (
            <div className="space-y-4 py-2">
              <div className="text-xs text-muted-foreground flex items-center justify-between">
                <span>File: <b>{preview.filename}</b> · {preview.total_rows} rows · {preview.headers.length} columns</span>
                {mappingSource === "saved" && (
                  <Badge className="bg-emerald-500 text-white gap-1"><Bookmark className="w-3 h-3" />Saved mapping applied</Badge>
                )}
                {mappingSource === "preset" && preview.matched_preset && (
                  <Badge className="bg-blue-500 text-white gap-1"><Wand2 className="w-3 h-3" />{preview.matched_preset.name} auto-detected</Badge>
                )}
                {mappingSource === "auto" && (
                  <Badge variant="outline" className="gap-1"><Sparkles className="w-3 h-3" />Auto-mapped</Badge>
                )}
              </div>

              {/* Preset picker */}
              <div className="border rounded-sm p-3 bg-muted/30">
                <Label className="text-[11px]">Quick apply — Bank preset</Label>
                <Select value={selectedPreset || "__none__"} onValueChange={(v) => applyPreset(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="preset-picker">
                    <SelectValue placeholder="Pick a bank preset…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None (manual mapping) —</SelectItem>
                    {presets.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Applying a preset overwrites the current mapping. You can still tweak each field below.
                </div>
              </div>

              {/* Column mapping */}
              <div className="border rounded-sm p-3">
                <div className="text-xs font-medium mb-2">Column mapping</div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(TARGET_LABELS).map((target) => (
                    <div key={target}>
                      <Label className="text-[11px]">{TARGET_LABELS[target]}</Label>
                      <Select
                        value={mapping[target] || "__none__"}
                        onValueChange={(v) => setMapping({ ...mapping, [target]: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger className="rounded-sm mt-1 h-9" data-testid={`map-${target}`}>
                          <SelectValue placeholder="— none —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— none —</SelectItem>
                          {preview.headers.filter((h) => h).map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">
                  Required: date + description AND either (debit+credit) or a single signed amount.
                </div>
              </div>

              {/* Preview of raw rows */}
              <div className="border rounded-sm overflow-x-auto max-h-40">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted">
                    <tr>
                      {preview.headers.map((h, i) => {
                        const mappedTo = Object.entries(mapping).find(([, v]) => v === h);
                        return (
                          <th key={i} className="text-left p-1.5 font-mono text-[10px] whitespace-nowrap">
                            {h}
                            {mappedTo && <span className="ml-1 text-emerald-600">→ {mappedTo[0]}</span>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview_rows.slice(0, 6).map((r, ri) => (
                      <tr key={ri} className="border-t">
                        {r.map((c, ci) => (
                          <td key={ci} className="p-1.5 whitespace-nowrap max-w-[180px] truncate">
                            {c === null || c === undefined ? "" : String(c)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <Checkbox id="save-map" checked={saveMapping} onCheckedChange={setSaveMapping} data-testid="save-mapping-check" />
                <label htmlFor="save-map" className="cursor-pointer">
                  Remember this mapping for future imports on this bank account
                </label>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="wiz-back-1">Back</Button>
                <Button
                  variant="outline"
                  onClick={saveMappingOnly}
                  disabled={busy || !selBank || !mapping.date || !mapping.description}
                  data-testid="wiz-save-mapping-only"
                  title="Saves the column template on this bank so future uploads auto-fill. Does NOT import the rows in the current file."
                >
                  <Bookmark className="w-3.5 h-3.5 mr-1" />
                  Save template only
                </Button>
                <Button
                  onClick={commitImport}
                  disabled={!mappingComplete || busy || !selBank}
                  data-testid="wiz-import"
                  className="bg-accent hover:bg-accent/90 text-white gap-1.5"
                  title="Parses all rows, auto-matches against open vouchers, and closes this wizard. You can review or run AI Suggest afterwards."
                >
                  <Save className="w-3.5 h-3.5" />
                  {busy ? "Saving…" : `Save Statement & Exit · ${preview.total_rows} row${preview.total_rows === 1 ? "" : "s"}`}
                </Button>
              </DialogFooter>
              <div className="text-[10px] text-muted-foreground text-center pt-1">
                <b>Save template only</b> saves just the column mapping. <b>Save Statement &amp; Exit</b> imports every row into the bank ledger, auto-matches known vouchers, and closes.
              </div>
              {!selBank && (
                <div className="text-xs text-destructive text-center">
                  Pick a bank account at the top of the page first.
                </div>
              )}
              {!mappingComplete && (
                <div className="text-xs text-muted-foreground text-center">
                  Map at least <b>date</b>, <b>description</b>, and either <b>debit+credit</b> or <b>amount</b>.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Save as rule dialog (Iter-129) ---- */}
      <Dialog open={!!ruleDraft} onOpenChange={(o) => !o && setRuleDraft(null)}>
        <DialogContent className="max-w-lg" data-testid="save-rule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />Save as recurring rule
            </DialogTitle>
          </DialogHeader>
          {ruleDraft && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-muted-foreground">
                Any future bank row whose description contains this pattern will auto-post to the target account. Edit the fields below before saving.
              </div>
              <div>
                <Label>Rule name</Label>
                <Input value={ruleDraft.name}
                       onChange={(e) => setRuleDraft({ ...ruleDraft, name: e.target.value })}
                       className="rounded-sm mt-1" data-testid="rule-draft-name" />
              </div>
              <div>
                <Label>Pattern (case-insensitive substring)</Label>
                <Input value={ruleDraft.patterns[0] || ""}
                       onChange={(e) => setRuleDraft({ ...ruleDraft, patterns: [e.target.value] })}
                       className="rounded-sm mt-1 font-mono" data-testid="rule-draft-pattern" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Auto-extracted from row description. Tweak to be broader or narrower.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Side</Label>
                  <Select value={ruleDraft.side}
                          onValueChange={(v) => setRuleDraft({ ...ruleDraft, side: v })}>
                    <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="rule-draft-side"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="debit">Debit (money out)</SelectItem>
                      <SelectItem value="credit">Credit (money in)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Input type="number" value={ruleDraft.priority}
                          onChange={(e) => setRuleDraft({ ...ruleDraft, priority: Number(e.target.value) })}
                          className="rounded-sm mt-1" />
                </div>
              </div>
              <div>
                <Label>Post to account *</Label>
                <Select value={ruleDraft.account_id}
                        onValueChange={(v) => setRuleDraft({ ...ruleDraft, account_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="rule-draft-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                  <SelectContent>
                    {accountsCache.filter((a) => !a.is_control).map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <span className="font-mono text-[10px] mr-1">{a.code}</span>{a.name}{" "}
                        <span className="text-[10px] text-muted-foreground">({a.account_type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-[11px] text-muted-foreground bg-accent/5 border rounded-sm p-2">
                💡 On save, this rule is also <b>applied immediately</b> to the current row —
                a voucher is posted and the entry is marked as matched.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleDraft(null)}>Cancel</Button>
            <Button onClick={saveRuleFromRow}
                    disabled={!ruleDraft?.name || !ruleDraft?.account_id || !ruleDraft?.patterns?.[0]}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="rule-draft-save">
              Save rule & post this row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Multi-bank Batch Upload (Iter-132) ---- */}
      <Dialog open={openBatch} onOpenChange={(o) => o ? setOpenBatch(true) : closeBatch()}>
        <DialogContent className="max-w-5xl" data-testid="batch-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-accent" /> Multi-bank Batch Upload
            </DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Drop 2-10 statements at once — HDFC current, ICICI savings, SBI overdraft. Each file is auto-matched to the right bank account by filename or column signature. Adjust anything before hitting Import.
            </div>
          </DialogHeader>

          {!batchPreview && (
            <div className="space-y-4 py-4">
              <label
                onClick={() => batchFileRef.current?.click()}
                className="cursor-pointer flex flex-col items-center justify-center gap-3 h-40 border-2 border-dashed rounded-sm bg-accent/5 hover:bg-accent/10 transition-colors"
                data-testid="batch-file-drop"
              >
                <div className="flex gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-accent" />
                  <Layers className="w-8 h-8 text-accent" />
                </div>
                <div className="text-sm">
                  <span className="text-accent font-medium">Click to pick multiple files</span>
                </div>
                <div className="text-xs text-muted-foreground">.csv · .xls · .xlsx · .pdf — any combination, one or many banks</div>
                <input
                  ref={batchFileRef}
                  type="file"
                  multiple
                  accept=".csv,.xls,.xlsx,.txt,.pdf"
                  className="hidden"
                  onChange={(e) => e.target.files?.length && runBatchPreview(e.target.files)}
                  data-testid="batch-file-input"
                />
              </label>
              {batchBusy && <div className="text-center text-xs text-accent"><Sparkles className="inline w-3 h-3 mr-1" />Parsing {batchFiles.length} file(s)…</div>}
            </div>
          )}

          {batchPreview && (
            <div className="space-y-3 py-2 max-h-[65vh] overflow-y-auto">
              <div className="text-xs text-muted-foreground">
                {batchPreview.files.length} file(s) parsed · pick or confirm the bank account per row.
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2">File</th>
                    <th className="text-left">Rows</th>
                    <th className="text-left">Bank account</th>
                    <th className="text-left">Auto-detected</th>
                    <th className="text-left">Preset</th>
                    <th className="text-left">Save mapping</th>
                  </tr>
                </thead>
                <tbody>
                  {batchPreview.files.map((f, idx) => (
                    <tr key={f.filename + idx} className="border-b align-top" data-testid={`batch-row-${idx}`}>
                      <td className="py-2 pr-2 max-w-[240px]">
                        <div className="font-mono text-xs break-all">{f.filename}</div>
                        {f.error && <div className="text-[10px] text-destructive mt-0.5">✗ {f.error}</div>}
                      </td>
                      <td className="py-2 text-xs">{f.total_rows ?? "—"}</td>
                      <td className="py-2 pr-2 min-w-[220px]">
                        <select
                          value={f.matched_bank_account_id || ""}
                          onChange={(e) => setBatchFile(idx, { matched_bank_account_id: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                          data-testid={`batch-bank-${idx}`}
                          disabled={!!f.error}
                        >
                          <option value="">— Pick bank account —</option>
                          {(batchPreview.accounts || []).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} · {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-2 text-[10px]">
                        {f.matched_by ? (
                          <span className="text-emerald-600">✓ {f.matched_by}</span>
                        ) : (
                          <span className="text-amber-600">Manual pick required</span>
                        )}
                        {f.matched_preset && (
                          <div className="text-blue-600 mt-0.5">
                            <Wand2 className="inline w-3 h-3 mr-0.5" />
                            {f.matched_preset.name} detected
                          </div>
                        )}
                        {f.saved_mapping && (
                          <div className="text-emerald-600 mt-0.5">
                            <Bookmark className="inline w-3 h-3 mr-0.5" />Saved mapping loaded
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2 min-w-[160px]">
                        <select
                          value=""
                          onChange={(e) => e.target.value && applyPresetToBatchFile(idx, e.target.value)}
                          className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                          disabled={!!f.error}
                          data-testid={`batch-preset-${idx}`}
                        >
                          <option value="">
                            {f.applied_preset ? `Override: ${f.applied_preset}` : "— Apply preset —"}
                          </option>
                          {presets.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 text-center">
                        <Checkbox
                          checked={!!f.save_mapping}
                          onCheckedChange={(v) => setBatchFile(idx, { save_mapping: !!v })}
                          disabled={!!f.error}
                          data-testid={`batch-save-${idx}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeBatch} data-testid="batch-cancel">
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            {batchPreview && (
              <Button variant="outline" onClick={resetBatch} data-testid="batch-reset">
                Pick different files
              </Button>
            )}
            <Button
              onClick={commitBatch}
              disabled={!batchPreview || batchBusy || batchPreview.files.filter((f) => !f.error && f.matched_bank_account_id).length === 0}
              className="bg-accent hover:bg-accent/90 text-white gap-1.5"
              data-testid="batch-import-commit"
              title="Imports every file into its matched bank account, auto-matches known vouchers, and closes this wizard."
            >
              <Save className="w-3.5 h-3.5" />
              {batchBusy ? "Saving…" : `Save & Exit · ${batchPreview?.files.filter((f) => !f.error && f.matched_bank_account_id).length || 0} file(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Iter-143 · Post-Import Success Dialog ---- */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="max-w-md" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-600" /> Statement imported
            </DialogTitle>
          </DialogHeader>
          {importResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-sm border p-3 bg-emerald-50/40">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Auto-matched</div>
                  <div className="text-2xl font-heading text-emerald-700 tabular-nums">{importResult.matched}</div>
                </div>
                <div className="rounded-sm border p-3 bg-amber-50/40">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Needs your attention</div>
                  <div className="text-2xl font-heading text-amber-700 tabular-nums">{importResult.unmatched}</div>
                </div>
              </div>
              {(importResult.auto_posted > 0 || importResult.queued_for_approval > 0 || importResult.parse_errors_count > 0) && (
                <div className="text-[11px] space-y-0.5 text-muted-foreground">
                  {importResult.auto_posted > 0 && <div>• {importResult.auto_posted} row(s) auto-posted via recurring bank rules</div>}
                  {importResult.queued_for_approval > 0 && <div>• {importResult.queued_for_approval} row(s) queued for approval (over threshold)</div>}
                  {importResult.parse_errors_count > 0 && <div>• {importResult.parse_errors_count} row(s) skipped — column parse errors</div>}
                  {importResult.mapping_saved && <div>• Column mapping saved as this bank's default template</div>}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                {importResult.unmatched > 0 ? "What would you like to do with the unmatched rows?" : "Every row was auto-matched — you can close this and move on."}
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setImportResult(null)} data-testid="ir-close">Close</Button>
            {importResult?.unmatched > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={async () => { setImportResult(null); await runSuggest(); }}
                  data-testid="ir-ai-suggest"
                  className="gap-1.5 border-sky-400 text-sky-700 hover:bg-sky-50"
                >
                  <Wand2 className="w-3.5 h-3.5" /> Run AI Suggest
                </Button>
                <Button
                  onClick={() => {
                    setImportResult(null);
                    // Jump to the first unmatched row's Create Voucher dialog
                    setTimeout(() => {
                      const btn = document.querySelector('[data-testid^="create-voucher-"]');
                      if (btn) { btn.click(); btn.scrollIntoView({ behavior: "smooth", block: "center" }); }
                    }, 150);
                  }}
                  data-testid="ir-create-vouchers"
                  className="bg-accent hover:bg-accent/90 text-white gap-1.5"
                >
                  <FileText className="w-3.5 h-3.5" /> Create Vouchers
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Iter-142 · Create Voucher dialog ---- */}
      <Dialog open={!!voucherFor} onOpenChange={(o) => !o && closeCreateVoucher()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-voucher-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-accent" /> Create Voucher from Bank Row
            </DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              A new voucher will be posted and this bank row will be linked to it. Bank leg is locked so numbers can never drift from your statement.
            </div>
          </DialogHeader>
          {voucherFor && vForm && (
            <div className="space-y-4">
              {/* Bank row summary */}
              <div className="border rounded-sm p-3 bg-muted/40 text-xs flex justify-between">
                <div>
                  <div className="font-medium">{voucherFor.description}</div>
                  <div className="text-muted-foreground mt-0.5">
                    {fmtDate(voucherFor.date)}{voucherFor.reference ? ` · ref ${voucherFor.reference}` : ""}
                  </div>
                </div>
                <div className="text-right tabular-nums font-mono text-sm">
                  {voucherFor.debit > 0 ? <span className="text-rose-600">DR {fmtINR(voucherFor.debit)}</span> : <span className="text-emerald-600">CR {fmtINR(voucherFor.credit)}</span>}
                </div>
              </div>

              {/* Header fields */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[11px]">Voucher type</Label>
                  <Select value={vForm.voucher_type} onValueChange={(v) => setVForm((f) => ({ ...f, voucher_type: v }))}>
                    <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="cv-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="payment">Payment (money out)</SelectItem>
                      <SelectItem value="receipt">Receipt (money in)</SelectItem>
                      <SelectItem value="contra">Contra (bank ↔ bank/cash)</SelectItem>
                      <SelectItem value="journal">Journal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Voucher date</Label>
                  <Input type="date" value={vForm.date} onChange={(e) => setVForm((f) => ({ ...f, date: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="cv-date" />
                </div>
                <div>
                  <Label className="text-[11px]">Reference / UTR / Cheque</Label>
                  <Input value={vForm.reference} onChange={(e) => setVForm((f) => ({ ...f, reference: e.target.value }))} className="rounded-sm mt-1 h-9 font-mono text-xs" data-testid="cv-ref" placeholder="Auto-filled from bank" />
                </div>
                <div>
                  <Label className="text-[11px]">Party (optional)</Label>
                  <Select value={vForm.party_id || "__none__"} onValueChange={(v) => setVForm((f) => ({ ...f, party_id: v === "__none__" ? "" : v }))}>
                    <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="cv-party"><SelectValue placeholder="— none —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {partiesCache.slice(0, 400).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="text-[10px] font-mono mr-1">{p.party_type?.[0]?.toUpperCase()}</span>{p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[11px]">Narration</Label>
                <Input value={vForm.narration} onChange={(e) => setVForm((f) => ({ ...f, narration: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="cv-narr" />
              </div>

              {/* Iter-143 · Bill-payment shortcut — appears the moment a party is selected */}
              {vForm.party_id && memoryHint && (
                <div className="rounded-sm border border-violet-300 bg-violet-50/60 px-3 py-2 flex items-center gap-2 text-xs" data-testid="cv-memory-hint">
                  <Bookmark className="w-3.5 h-3.5 text-violet-700" />
                  <div className="flex-1 text-violet-900">
                    {memoryHint.autofilled ? <b>Auto-filled counter account: </b> : <b>Last time: </b>}
                    <span className="font-mono text-[11px]">{memoryHint.acc.code}</span> · {memoryHint.acc.name}
                    <span className="text-[10px] text-violet-700/70 ml-2">used {memoryHint.count}× · last {fmtDate(memoryHint.last_used_at)}</span>
                  </div>
                  {memoryHint.autofilled && (
                    <Button size="sm" variant="ghost" onClick={() => {
                        setLine(1, { account_id: "" });
                        setMemoryHint((m) => m ? { ...m, autofilled: false } : m);
                      }}
                      className="rounded-sm h-6 text-[10px] px-2 text-violet-700 hover:bg-violet-100"
                      data-testid="cv-memory-clear">Undo</Button>
                  )}
                </div>
              )}

              {/* Iter-143 · Bill-payment shortcut — appears the moment a party is selected */}
              {vForm.party_id && (
                <div className="border rounded-sm bg-sky-50/30" data-testid="cv-bills-panel">
                  <div className="px-3 py-2 border-b flex items-center gap-2 bg-sky-50/60">
                    <FileText className="w-3.5 h-3.5 text-sky-700" />
                    <div className="text-xs font-medium text-sky-900">Open bills / invoices for this party</div>
                    {openBillsBusy && <span className="text-[10px] text-muted-foreground">loading…</span>}
                    <div className="flex-1" />
                    {openBills.length > 0 && (
                      <Button size="sm" variant="outline" onClick={applyPickedBills} className="rounded-sm h-7 text-[10px] gap-1" data-testid="cv-apply-bills">
                        <Check className="w-3 h-3" /> Allocate selected → lines
                      </Button>
                    )}
                  </div>

                  {/* Iter-144 · AI Smart Match banner — one-tap accept for auto-picked bills */}
                  {smartMatchInfo && (
                    <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2" data-testid="cv-smart-match-banner">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                      <div className="flex-1 text-[11px] text-emerald-900">
                        <b>AI Smart Match:</b> auto-selected <b>{smartMatchInfo.count}</b> bill{smartMatchInfo.count > 1 ? "s" : ""} totalling <b>{fmtINR(smartMatchInfo.total)}</b> — matches the bank amount exactly ({smartMatchInfo.mode === "single" ? "single bill" : smartMatchInfo.mode === "all" ? "all open bills" : `subset match`}).
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setPickedBills({}); setSmartMatchInfo(null); }}
                        className="rounded-sm h-6 text-[10px] px-2 text-emerald-700 hover:bg-emerald-100"
                        data-testid="cv-smart-match-clear">Undo</Button>
                      <Button size="sm" onClick={applyPickedBills}
                        className="rounded-sm h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="cv-smart-match-apply">
                        <Check className="w-3 h-3 mr-1" />Accept
                      </Button>
                    </div>
                  )}

                  {openBills.length === 0 ? (
                    <div className="p-3 text-[11px] text-muted-foreground text-center">
                      {openBillsBusy ? "…" : "No open bills / invoices for this party."}
                    </div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-muted/10">
                        <tr>
                          <th className="text-left px-2 py-1.5 w-8"></th>
                          <th className="text-left px-2 py-1.5">Doc #</th>
                          <th className="text-left px-2 py-1.5">Date</th>
                          <th className="text-right px-2 py-1.5">Total ₹</th>
                          <th className="text-right px-2 py-1.5">Balance ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openBills.slice(0, 12).map((b) => (
                          <tr key={b.id} className="border-t hover:bg-muted/20 cursor-pointer" onClick={() => setPickedBills((s) => ({ ...s, [b.id]: !s[b.id] }))} data-testid={`cv-bill-${b.doc_no}`}>
                            <td className="px-2 py-1.5"><Checkbox checked={!!pickedBills[b.id]} onCheckedChange={() => setPickedBills((s) => ({ ...s, [b.id]: !s[b.id] }))} /></td>
                            <td className="px-2 py-1.5 font-mono">{b.doc_no}</td>
                            <td className="px-2 py-1.5">{fmtDate(b.date)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{fmtINR(b.grand_total)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmtINR(b.balance_due)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {Object.keys(pickedBills).filter((k) => pickedBills[k]).length > 0 && (
                        <tfoot className="bg-sky-50/50 border-t">
                          <tr>
                            <td colSpan={4} className="px-2 py-1.5 text-right text-[10px] font-mono uppercase text-sky-800">Selected</td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium text-sky-800">
                              {fmtINR(openBills.filter((b) => pickedBills[b.id]).reduce((a, b) => a + (b.balance_due || 0), 0))}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  )}
                </div>
              )}

              {/* Iter-143 · TDS auto-deduct chip — appears when party has a default TDS section */}
              {tdsInfo && (
                <div className={`rounded-sm border p-3 flex items-center gap-3 text-xs ${tdsApplied ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"}`} data-testid="cv-tds-chip">
                  <Sparkles className={`w-4 h-4 ${tdsApplied ? "text-emerald-600" : "text-amber-600"}`} />
                  <div className="flex-1">
                    <div className="font-medium">TDS Sec {tdsInfo.section} @ {tdsInfo.rate}% detected on this party</div>
                    <div className="text-[10px] text-muted-foreground">
                      {tdsApplied ? "Counter line grossed-up and Cr TDS Payable line added." :
                       tdsInfo.payable_account_id ? `Toggle to gross-up the counter line and inject a Cr ${tdsInfo.payable_account_name} line automatically.`
                                                  : `⚠ TDS Payable — Sec ${tdsInfo.section} ledger missing in Chart of Accounts. Create it before applying.`}
                    </div>
                  </div>
                  <Button size="sm" variant={tdsApplied ? "outline" : "default"} onClick={toggleTds} disabled={!tdsInfo.payable_account_id}
                             className={`rounded-sm h-7 text-[10px] gap-1 ${tdsApplied ? "" : "bg-amber-600 hover:bg-amber-700 text-white"}`}
                             data-testid="cv-tds-toggle">
                    {tdsApplied ? "Remove TDS" : "Apply TDS"}
                  </Button>
                </div>
              )}

              {/* Voucher lines */}
              <div className="border rounded-sm">
                <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
                  <div className="text-xs font-medium">Ledger lines</div>
                  <Button size="sm" variant="outline" onClick={addSplitLine} className="rounded-sm h-7 text-[10px] gap-1" data-testid="cv-add-line">
                    <Sparkles className="w-3 h-3" /> Split further
                  </Button>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-muted/10">
                    <tr>
                      <th className="text-left px-2 py-1.5 w-8"></th>
                      <th className="text-left px-2 py-1.5">Account</th>
                      <th className="text-left px-2 py-1.5">Party</th>
                      <th className="text-left px-2 py-1.5">Dimension</th>
                      <th className="text-right px-2 py-1.5 w-24">Dr ₹</th>
                      <th className="text-right px-2 py-1.5 w-24">Cr ₹</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {vForm.lines.map((l, idx) => {
                      const isBankLeg = idx === 0;
                      return (
                        <tr key={idx} className="border-t align-top" data-testid={`cv-line-${idx}`}>
                          <td className="px-2 py-1.5 text-[10px] font-mono text-muted-foreground">{isBankLeg ? "BANK" : idx}</td>
                          <td className="px-2 py-1.5">
                            {isBankLeg ? (
                              <div className="text-xs">
                                {(accountsCache.find((a) => a.id === l.account_id) || {}).name || "Bank"}
                                <div className="text-[10px] font-mono text-muted-foreground">locked</div>
                              </div>
                            ) : (
                              <Select value={l.account_id} onValueChange={(v) => setLine(idx, { account_id: v })}>
                                <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`cv-acc-${idx}`}><SelectValue placeholder="Pick account…" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                  {accountsCache.filter((a) => !a.is_control && a.id !== voucherFor.bank_account_id).map((a) => (
                                    <SelectItem key={a.id} value={a.id}>
                                      <span className="font-mono text-[10px] mr-1">{a.code}</span>{a.name}
                                      <span className="text-[10px] text-muted-foreground ml-1">({a.account_type})</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            {isBankLeg ? <span className="text-[10px] text-muted-foreground">—</span> : (
                              <Select value={l.party_id || "__none__"} onValueChange={(v) => setLine(idx, { party_id: v === "__none__" ? "" : v })}>
                                <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="— none —" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                  <SelectItem value="__none__">— none —</SelectItem>
                                  {partiesCache.slice(0, 400).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            {isBankLeg ? <span className="text-[10px] text-muted-foreground">—</span> : (
                              <Select value={l.cost_center_id || "__none__"} onValueChange={(v) => setLine(idx, { cost_center_id: v === "__none__" ? "" : v })}>
                                <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="— none —" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                  <SelectItem value="__none__">— none —</SelectItem>
                                  {costCentresCache.map((c) => <SelectItem key={c.id} value={c.id}>{c.code || c.name} · {c.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <Input type="number" value={l.debit || ""}
                                    onChange={(e) => setLine(idx, { debit: Number(e.target.value) || 0 })}
                                    disabled={isBankLeg}
                                    className="rounded-sm h-8 text-xs tabular-nums text-right" data-testid={`cv-dr-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <Input type="number" value={l.credit || ""}
                                    onChange={(e) => setLine(idx, { credit: Number(e.target.value) || 0 })}
                                    disabled={isBankLeg}
                                    className="rounded-sm h-8 text-xs tabular-nums text-right" data-testid={`cv-cr-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {!isBankLeg && vForm.lines.length > 2 && (
                              <button onClick={() => removeSplitLine(idx)} className="text-rose-600 hover:text-rose-700" data-testid={`cv-del-${idx}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/30 border-t">
                    <tr>
                      <td colSpan={4} className="px-2 py-2 text-right text-[10px] font-mono uppercase text-muted-foreground">Totals</td>
                      <td className="px-2 py-2 text-right tabular-nums font-mono">
                        {fmtINR(vForm.lines.reduce((a, l) => a + (Number(l.debit) || 0), 0))}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-mono">
                        {fmtINR(vForm.lines.reduce((a, l) => a + (Number(l.credit) || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Balance indicator */}
              {(() => {
                const totalDr = vForm.lines.reduce((a, l) => a + (Number(l.debit) || 0), 0);
                const totalCr = vForm.lines.reduce((a, l) => a + (Number(l.credit) || 0), 0);
                const diff = Math.abs(totalDr - totalCr);
                if (diff < 0.01) return <div className="text-[11px] text-emerald-700 flex items-center gap-1"><Check className="w-3 h-3" /> Balanced.</div>;
                return <div className="text-[11px] text-rose-600 flex items-center gap-1">⚠ Unbalanced — difference ₹{diff.toFixed(2)}. Fix the split before saving.</div>;
              })()}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeCreateVoucher} data-testid="cv-cancel">Cancel</Button>
            <Button
              onClick={commitCreateVoucher}
              disabled={vBusy || !vForm}
              className="bg-accent hover:bg-accent/90 text-white gap-1.5"
              data-testid="cv-save">
              <Save className="w-3.5 h-3.5" />
              {vBusy ? "Posting…" : "Save Voucher & Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!pickForEntry} onOpenChange={(o) => !o && setPickForEntry(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto">
          {pickForEntry && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Pick voucher to match</SheetTitle></SheetHeader>
              <div className="mt-3 border rounded-sm p-3 bg-muted/40 text-sm">
                <div>{pickForEntry.description}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {fmtDate(pickForEntry.date)} · {pickForEntry.debit ? `DR ${fmtINR(pickForEntry.debit)}` : `CR ${fmtINR(pickForEntry.credit)}`}
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {candidates.length === 0 && <div className="text-center text-muted-foreground py-6 border rounded-sm">No candidate vouchers. Post a payment/receipt first.</div>}
                {candidates.map((c) => (
                  <button key={c.voucher_id} onClick={() => match(c.voucher_id)} data-testid={`candidate-${c.voucher_number}`}
                          className={`w-full text-left p-3 border rounded-sm hover:bg-muted flex items-center justify-between ${c.score >= 1 ? "border-emerald-500/40 bg-emerald-500/5" : ""}`}>
                    <div>
                      <div className="font-mono text-xs">{c.voucher_number}</div>
                      <div className="text-xs text-muted-foreground">{c.date} · {c.narration || c.voucher_type}</div>
                    </div>
                    <div className="text-right">
                      <div className="tabular-nums font-mono">{fmtINR(c.amount)}</div>
                      {c.score >= 1 && <div className="text-[10px] font-mono uppercase text-emerald-600">Exact match</div>}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
